/**
 * Prefill inputs according to another input's value.
 *
 * For example changing the value of a <select> element could fill some
 * text fields according to the new value of the <select> element.
 */

import delegateListener from '../modules/delegate-listener';

class Prefill {
  constructor() {
    this.prefillSelector = '[data-prefill-data]';
  }

  init() {
    document.body.addEventListener('change', delegateListener(
      this.prefillSelector,
      this._handleValueChange.bind(this))
    );
  }

  /**
   * Handles the 'change' event of the prefill element.
   *
   * @param {Event} event The 'change' event
   */
  _handleValueChange(event) {
    const prefillElement = event.delegateTarget || event.currentTarget;

    const prefillData = this._getPrefillData(prefillElement);

    let prefillTarget = {};
    try {
      prefillTarget = JSON.parse(prefillElement.dataset.prefillTarget);
    } catch (error) {
      /* Could not parse prefillTarget. Make sure that the value of the data-prefill-target attribute is valid JSON. */
      return;
    }
    this._prefillTargets(prefillTarget, prefillData);
  }

  /**
   * Returns the prefill data corresponding the current value of the element.
   *
   * @param {DOMElement} prefillElement The element with a data-prefill-data attribute
   * @returns {Object} An object containing prefill data on success. Empty object if
   *                   - No data for current value is found
   *                   - Value of the data-prefill-data attribute is not valid JSON
   */
  _getPrefillData(prefillElement) {
    let prefillData = {};

    try {
      prefillData = JSON.parse(prefillElement.dataset.prefillData);
    } catch (error) {
      /* Could not parse prefillData. Make sure that the value of the data-prefill-data attribute is valid JSON. */
      return {};
    }

    const newValue = prefillElement.value;

    if (!newValue || typeof prefillData[newValue] !== 'object' || !prefillData[newValue]) {
      return {};
    }

    return prefillData[newValue];
  }

  /**
   * Prefills the target elements with data
   *
   * @param {Object} target An object that maps each property of the data object to an element selector
   * @param {Object} data An object containing the data to prefill into the target elements
   */
  _prefillTargets(target, data) {
    // Loop through each target
    Object.keys(target).forEach(fieldName => {
      // Find new value to prefill into the target.
      // If no value is found, prefill with empty string.
      let newValue = '';
      if (typeof data[fieldName] !== 'undefined') {
        newValue = data[fieldName] || '';
      }
      // Set new value
      document.querySelector(target[fieldName]).value = newValue;
    });
  }
}

export default Prefill;
